import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { api } from "@/lib/api";
import { colors, inr } from "@/theme";

export function SearchSuggest({ width = 240, autoFocus = false }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const boxRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const fetchSuggest = useCallback((term) => {
    if (!term.trim()) { setResults([]); setLoading(false); return; }
    setLoading(true);
    api(`/search/suggest?q=${encodeURIComponent(term.trim())}`, { auth: false })
      .then((d) => { setResults(d.products || []); setOpen(true); })
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, []);

  const onChange = (e) => {
    const val = e.target.value;
    setQ(val); setActive(-1); setOpen(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => fetchSuggest(val), 220);
  };

  const go = (id) => { setOpen(false); setQ(""); setResults([]); navigate(`/product/${id}`); };
  const submit = () => { if (q.trim()) { setOpen(false); navigate(`/search?q=${encodeURIComponent(q.trim())}`); } };

  const onKey = (e) => {
    if (!open || results.length === 0) { if (e.key === "Enter") submit(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, -1)); }
    else if (e.key === "Enter") { e.preventDefault(); active >= 0 ? go(results[active].id) : submit(); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={boxRef} style={{ position: "relative", width }}>
      <Search size={16} color={colors.mutedText} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
      <input
        data-testid="search-suggest-input"
        value={q}
        autoFocus={autoFocus}
        onChange={onChange}
        onKeyDown={onKey}
        onFocus={() => q && setOpen(true)}
        placeholder="Search products…"
        style={{ width: "100%", padding: "10px 32px 10px 34px", border: `1px solid ${colors.border}`, borderRadius: 999, background: colors.surfaceSecondary, fontSize: 13.5, outline: "none", fontFamily: "DM Sans", color: colors.onSurface }}
      />
      {q && <button onClick={() => { setQ(""); setResults([]); setOpen(false); }} aria-label="Clear" data-testid="search-clear" style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: colors.mutedText, display: "flex" }}><X size={15} /></button>}

      {open && q.trim() && (
        <div data-testid="search-dropdown" style={{ position: "absolute", top: "calc(100% + 8px)", left: 0, right: 0, minWidth: 300, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, boxShadow: "0 16px 40px rgba(44,41,37,0.16)", overflow: "hidden", zIndex: 60, maxHeight: 380, overflowY: "auto" }}>
          {loading && results.length === 0 ? (
            <div style={{ padding: 16, fontSize: 13, color: colors.mutedText }}>Searching…</div>
          ) : results.length === 0 ? (
            <div data-testid="search-no-results" style={{ padding: 16, fontSize: 13, color: colors.mutedText }}>No matches. Press Enter to search all.</div>
          ) : (
            results.map((p, i) => (
              <button key={p.id} data-testid={`suggest-item-${p.id}`} onMouseEnter={() => setActive(i)} onClick={() => go(p.id)}
                style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left", padding: "10px 14px", border: "none", cursor: "pointer", background: i === active ? colors.surfaceSecondary : "transparent" }}>
                <div style={{ width: 42, height: 42, borderRadius: 8, overflow: "hidden", background: colors.surfaceSecondary, flexShrink: 0 }}>
                  {p.image && <img src={p.image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
                </div>
                <span className="font-body clamp-1" style={{ flex: 1, fontSize: 13.5, color: colors.onSurface }}>{p.name}</span>
                <span className="font-body" style={{ fontSize: 13, fontWeight: 600 }}>{inr(p.price)}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
